import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WhatsAppWidget } from '../components/WhatsAppWidget';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { LeadForm } from '../components/LeadForm';
import {
  Clock,
  ShieldCheck,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Phone,
  MessageCircle,
  Languages,
  Award,
  Sparkles,
  Layers,
} from 'lucide-react';
import { getCourseBySlug, courses, softwareLogos, faqs as siteFaqs } from '../mock';
import { SITE, getWhatsAppUrl } from '../lib/site';
import { applyPageMeta, buildBreadcrumbSchema, buildCourseSchema, buildFaqSchema } from '../lib/seo';

const updateMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const CoursePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const course = getCourseBySlug(slug);

  const scrollToForm = () => {
    const el = document.getElementById('lead-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!course) return;
    const prevTitle = document.title;

    applyPageMeta({
      title: course.seo.title,
      description: course.seo.description,
      keywords: course.seo.keywords,
      path: `/courses/${course.slug}`,
      ogType: 'article',
      schemas: {
        'course-jsonld': buildCourseSchema(course),
        'breadcrumb-jsonld': buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Courses', path: '/#courses' },
          { name: course.title, path: `/courses/${course.slug}` },
        ]),
        'faq-jsonld': buildFaqSchema(siteFaqs.slice(0, 5)),
      },
    });

    return () => {
      document.title = prevTitle;
    };
  }, [course]);

  if (!course) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center px-6">
        <h1 className="text-4xl font-bold mb-4">Course not found</h1>
        <p className="text-gray-400 mb-8">The course you&apos;re looking for doesn&apos;t exist.</p>
        <Button
          onClick={() => navigate('/')}
          className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-5"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to home
        </Button>
      </div>
    );
  }

  const whatsappUrl = getWhatsAppUrl();
  const related = courses.filter((c) => c.slug !== course.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <AnnouncementBar />
      <Header onConsultationClick={scrollToForm} />

      {/* HERO */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-slate-900 to-orange-900/30 z-0"></div>
        <div
          className="absolute inset-0 opacity-10 z-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '100px 100px',
          }}
        ></div>
        <div className="absolute top-40 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm text-gray-400" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-2">
              <li>
                <Link to="/" className="hover:text-white">Home</Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/#courses" className="hover:text-white">Courses</Link>
              </li>
              <li>/</li>
              <li className="text-white truncate max-w-[60vw]" aria-current="page">{course.title}</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <div className="flex flex-wrap gap-2 mb-6">
                {course.placement && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Placement Assistance<sup className="ml-0.5 text-amber-300">*</sup>
                  </span>
                )}
                {course.addOn && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-400/30">
                    <Sparkles className="h-3.5 w-3.5" />
                    Skill Upgrade
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                  <Clock className="h-3.5 w-3.5" />
                  {course.duration}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                {course.title}
              </h1>
              <p className="text-xl md:text-2xl text-purple-300 italic mb-6">{course.tagline}</p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl">{course.overview}</p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={scrollToForm}
                  data-testid="coursepage-request-fee-cta"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 text-lg rounded-full border-0"
                >
                  Request Fee Details
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="coursepage-whatsapp-cta">
                  <Button
                    variant="outline"
                    className="bg-white/5 border-white/20 hover:bg-white/10 text-white px-8 py-6 text-lg rounded-full w-full"
                  >
                    <MessageCircle className="mr-2 h-5 w-5 text-emerald-400" />
                    Chat on WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            {/* Right side: meta card */}
            <aside className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 sticky top-32">
                <h3 className="text-sm uppercase tracking-widest text-purple-300 font-bold mb-4">Program at a glance</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Duration</div>
                      <div className="text-white font-semibold">{course.durationDetail}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Layers className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Modules</div>
                      <div className="text-white font-semibold">
                        {course.modules.length} {course.moduleChoice ? '(pick one)' : ''}
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Award className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Certification</div>
                      <div className="text-white font-semibold">Course Certificate{course.modules.some(m => m.name.startsWith('Internship')) ? ' + Internship Certificate' : ''}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Placement</div>
                      <div className="text-white font-semibold">
                        {course.placement ? 'Placement Assistance offered' : 'Skill upgrade. No placement'}
                      </div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Languages className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-400">Languages</div>
                      <div className="text-white font-semibold">{course.languages.join(', ')}</div>
                    </div>
                  </li>
                </ul>
                <Button
                  onClick={scrollToForm}
                  data-testid="coursepage-sidebar-fee-cta"
                  className="w-full mt-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-5 rounded-xl"
                >
                  Request Fee Details
                </Button>
                <a href={`tel:${SITE.phone}`} className="block mt-3" data-testid="coursepage-sidebar-call-cta">
                  <Button variant="outline" className="w-full bg-transparent border-white/20 hover:bg-white/10 text-white py-5 rounded-xl">
                    <Phone className="mr-2 h-4 w-4" />
                    Talk to Admissions Office
                  </Button>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <Layers className="h-5 w-5 text-purple-400" />
              <span className="uppercase tracking-widest text-xs font-bold text-purple-300">
                {course.moduleChoice ? 'Choose Your Module' : 'What You\'ll Master'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-10">
              {course.moduleChoice ? 'Pick one editor. Go deep. Get placed.' : 'Software you will master'}
            </h2>

            {/* Software logo grid with Hero-style hover effect. Skips non-software modules (e.g. internship). */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6 md:gap-8 max-w-4xl mx-auto mb-14">
              {course.modules.filter((mod) => softwareLogos[mod.name]).map((mod, i) => {
                const logo = softwareLogos[mod.name];
                return (
                  <div
                    key={i}
                    data-testid={`coursepage-software-${i}`}
                    className="group cursor-pointer relative flex items-center justify-center"
                  >
                    <div className="relative p-1">
                      <img
                        src={logo.logo}
                        alt={logo.label}
                        loading="lazy"
                        className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-2xl transition-all duration-500 ease-out group-hover:scale-125 group-hover:drop-shadow-[0_0_20px_rgba(168,85,247,0.8)] group-hover:-translate-y-2 group-hover:brightness-110"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/120/9333ea/ffffff?text=' + (logo.label || mod.name).charAt(0);
                        }}
                      />
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-4 opacity-0 scale-90 invisible group-hover:opacity-100 group-hover:scale-100 group-hover:visible transition-all duration-300 pointer-events-none z-50 whitespace-nowrap">
                      <div className="bg-white/95 backdrop-blur-sm text-slate-900 text-xs font-semibold px-4 py-2 rounded-lg shadow-2xl">
                        {logo.label}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-[-1px]">
                          <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[5px] border-b-white/95"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Module details grid — only software modules. Non-software items (e.g. internship) are summarised below. */}
            <div className="grid md:grid-cols-2 gap-5">
              {course.modules.filter((mod) => softwareLogos[mod.name]).map((mod, i) => (
                <div
                  key={i}
                  className="group bg-white/5 hover:bg-white/[0.07] border border-white/10 hover:border-purple-500/40 rounded-2xl p-6 transition-all duration-300"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center font-bold text-white text-sm">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-white group-hover:text-purple-300 transition-colors leading-tight">
                      {mod.name}
                    </h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-sm">{mod.description}</p>
                </div>
              ))}
            </div>

            {course.moduleChoice && (
              <p className="text-center text-purple-300/80 mt-8 text-sm italic">
                Choose one editor at enrolment. Duration adjusts based on your pick.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* PLACEMENT ASSURANCE BLOCK */}
      {course.placement && (
        <section className="py-16 bg-slate-900">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-4xl mx-auto bg-gradient-to-br from-emerald-500/10 to-purple-500/10 border border-emerald-400/30 rounded-3xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <ShieldCheck className="h-12 w-12 text-emerald-400 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-3">Placement Assistance<sup className="ml-1 text-amber-300 text-base">*</sup></h2>
                  <p className="text-gray-300 leading-relaxed mb-2">
                    Hiring partners send their requirements directly to our placement cell, often before those roles are posted publicly. Our students get first look.
                  </p>
                  <p className="text-xs text-gray-500 italic mb-4">
                    *Placement Assistance Guarantee is offered on select programs. Check with the Admissions Office for details.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4 mt-6">
                    {[
                      ['125+', 'Hiring Partners'],
                      ['500+', 'Students Placed'],
                      ['750+', 'Students Trained'],
                    ].map(([n, t]) => (
                      <div key={t} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-emerald-300">{n}</div>
                        <div className="text-xs text-gray-400 mt-1">{t}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHY MATCHFRAME bullets */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center">
              Why students pick this program
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                'Hands-on training on industry footage. Not toy projects.',
                'Apple Mac workstations and studio-grade infrastructure.',
                'Award-winning trainers with 25+ years of real production experience.',
                'Small batch sizes with personal 1-on-1 mentoring.',
                'Portfolio reviews from working professionals.',
                'Direct access to studios actively hiring editors.',
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-5">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200">{line}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RELATED COURSES */}
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-6 lg:px-12">
          <h2 className="text-3xl font-bold mb-8">Explore other programs</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((c) => (
              <Link
                key={c.slug}
                to={`/courses/${c.slug}`}
                className="block bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-purple-500/40 rounded-2xl p-6 transition-all"
                data-testid={`coursepage-related-${c.slug}`}
              >
                <div className="text-xs uppercase tracking-widest text-purple-300 font-bold mb-2">
                  {c.tier === 'short' ? 'Short Course' : 'Diploma'}
                </div>
                <h3 className="text-xl font-bold mb-2 group-hover:text-purple-300">{c.title}</h3>
                <div className="text-sm text-gray-400 flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4" />
                  {c.duration}
                </div>
                <p className="text-sm text-gray-300 line-clamp-3">{c.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <LeadForm />

      <Footer />
      <WhatsAppWidget />
    </div>
  );
};

export default CoursePage;
